export type Color = "gray" | "brown" | "orange" | "yellow" | "green" | "blue" | "purple" | "pink" | "red";
export const COLORS: Color[] = ["gray", "brown", "orange", "yellow", "green", "blue", "purple", "pink", "red"];

export type EntityType = "epic" | "sprint" | "task";
/** Items that can have custom properties; expense properties are only visible to owners. */
export type PropertyEntity = EntityType | "expense";
export type Role = "owner" | "member" | "viewer";
export type PropertyType = "select" | "multi_select" | "date";
export type StageKind = "normal" | "in_progress" | "qa" | "done";

export interface User {
  id: string;
  email: string;
  name: string;
  isAdmin: boolean;
  isActive: boolean;
  createdAt: string;
}

export interface Project {
  id: string;
  name: string;
  key: string;
  description: string;
  taskSeq: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectListItem extends Project {
  role: Role;
  memberCount: number;
  epicCount: number;
  taskCount: number;
}

export interface PropertyOption {
  id: string;
  label: string;
  color: Color;
  position: number;
}

export interface Property {
  id: string;
  entityType: PropertyEntity;
  name: string;
  type: PropertyType;
  position: number;
  options: PropertyOption[];
}

export interface Member {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
  role: Role;
  joinedAt: string;
}

export interface Stage {
  id: string;
  projectId: string;
  name: string;
  color: Color;
  kind: StageKind;
  position: number;
}

export interface ListItem {
  id: string;
  projectId: string;
  name: string;
  color: Color;
  position: number;
}

export interface ProjectBundle {
  project: Project;
  role: Role;
  properties: Property[];
  members: Member[];
  stages: Stage[];
  sprintStatuses: ListItem[];
  assigneeStatuses: ListItem[];
}

/** propertyId -> option ids, or a single YYYY-MM-DD for date properties */
export type Values = Record<string, string[]>;

// BlockNote document JSON
export type Body = unknown[] | null;

/** "epic" for regular epics; the others are the built-in task areas every project has. */
export type EpicArea = "epic" | "management" | "infra";

export interface EpicListItem {
  id: string;
  projectId: string;
  title: string;
  area: EpicArea;
  position: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  sprintCount: number;
  taskCount: number;
  doneCount: number;
  values: Values;
}

export interface Epic {
  id: string;
  projectId: string;
  title: string;
  area: EpicArea;
  body: Body;
  position: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  values: Values;
}

export interface SprintListItem {
  id: string;
  projectId: string;
  epicId: string;
  epicTitle: string;
  title: string;
  goal: string;
  statusId: string | null;
  startDate: string | null;
  endDate: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  taskCount: number;
  doneCount: number;
  values: Values;
}

export interface Sprint extends Omit<SprintListItem, "taskCount" | "doneCount"> {
  body: Body;
}

export interface Assignee {
  userId: string;
  statusId: string | null;
  assignedAt: string;
}

export interface TimeEntry {
  userId: string;
  closedSeconds: number;
  openSince: string | null;
}

export interface TaskListItem {
  id: string;
  projectId: string;
  number: number;
  epicId: string;
  sprintId: string | null;
  stageId: string;
  title: string;
  position: number;
  inProgressAt: string | null;
  qaReachedAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  commentCount: number;
  prLinkCount: number;
  values: Values;
  assignees: Assignee[];
  time: TimeEntry[];
}

export interface Session {
  id: string;
  taskId: string;
  userId: string;
  startedAt: string;
  endedAt: string | null;
}

export interface PRLink {
  id: string;
  taskId: string;
  url: string;
  createdBy: string | null;
  createdAt: string;
}

export interface Task extends Omit<TaskListItem, "commentCount" | "prLinkCount"> {
  body: Body;
  sessions: Session[];
  prLinks: PRLink[];
}

export interface Comment {
  id: string;
  projectId: string;
  entityType: EntityType;
  entityId: string;
  authorId: string | null;
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface Activity {
  id: string;
  projectId: string;
  entityType: EntityType;
  entityId: string;
  actorId: string | null;
  action: string;
  data: Record<string, unknown>;
  createdAt: string;
  title?: string;
}

export interface AppNotification {
  id: string;
  userId: string;
  actorId: string | null;
  projectId: string;
  projectName: string;
  entityType: EntityType;
  entityId: string;
  kind: "mentioned" | "commented" | "assigned" | "unassigned" | "stage_changed";
  data: { title?: string; number?: number; key?: string; snippet?: string; from?: string; to?: string };
  readAt: string | null;
  createdAt: string;
}

export interface FileLink {
  entityType: EntityType;
  entityId: string;
  title: string;
}

export interface ProjectFile {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  description: string;
  uploadedBy: string | null;
  createdAt: string;
  updatedAt: string;
  links: FileLink[];
}

export interface SearchResult {
  entityType: EntityType;
  id: string;
  title: string;
  number: number | null;
  epicId: string | null;
  updatedAt: string;
}

// Finance (owners only). Amounts are integers in minor units (cents).

export interface FinanceSettings {
  projectId: string;
  agreedAmount: number;
  currency: string;
  scopePropertyId: string | null;
  complexityPropertyId: string | null;
  updatedAt: string;
}

export interface ExperienceLevel {
  id: string;
  projectId: string;
  name: string;
  color: Color;
  multiplier: number;
  position: number;
}

export interface ExpenseAttachment {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  createdBy: string | null;
  createdAt: string;
}

export interface ExpenseShare {
  expenseId: string;
  userId: string;
  share: number;
  amount: number;
}

/** Money a member owes for an expense: to a teammate who covered for them, or (creditor null) not covered by anyone. */
export interface Debt {
  id: string;
  expenseId: string;
  debtor: string;
  creditor: string | null;
  amount: number;
  outstanding: number;
  createdAt: string;
}

export interface Repayment {
  receiptId: string;
  expenseId: string;
  debtor: string;
  creditor: string | null;
  amount: number;
  createdAt: string;
}

/** A member's money across payments, withdrawals and expenses. */
export interface MemberMoney {
  userId: string;
  earned: number;
  withdrawn: number;
  /** paid toward expenses (their parts, covering others, repaying), minus what was paid back to them */
  paid: number;
  owes: number;
  owedToThem: number;
  /** money actually held for them */
  cash: number;
  /** cash − owes + owed to them; negative when they owe more than they hold */
  balance: number;
  /** what can be withdrawn now */
  available: number;
}

export interface Expense {
  id: string;
  projectId: string;
  title: string;
  amount: number;
  spentOn: string;
  note: string;
  /**
   * "balances": charged to members' balances by their current shares when added.
   * "receipts": recorded before that rule; taken out of received payments before the split.
   */
  funding: "balances" | "receipts";
  /** each member's part (balances-funded expenses) */
  shares: ExpenseShare[];
  /** receipts-funded expenses: how much has been taken out of received money */
  covered: number;
  /** supporting documents (PDF, PNG or JPEG) */
  attachments: ExpenseAttachment[];
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  values: Values;
}

export interface PaymentAllocation {
  receiptId: string;
  userId: string;
  weight: number;
  share: number;
  amount: number;
}

/** Money received against a scheduled payment: all of it or a part. Its split is locked when recorded. */
export interface PaymentReceipt {
  id: string;
  paymentId: string;
  amount: number;
  receivedOn: string;
  /** expenses taken out of this receipt before the split */
  expensesDeducted: number;
  /** what was split between members */
  distributed: number;
  lockedAt: string;
  createdBy: string | null;
  createdAt: string;
  allocations: PaymentAllocation[];
}

export interface Payment {
  id: string;
  projectId: string;
  title: string;
  amount: number;
  dueDate: string | null;
  note: string;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  /** sum of the receipts */
  received: number;
  receipts: PaymentReceipt[];
}

export interface Payout {
  id: string;
  projectId: string;
  userId: string;
  amount: number;
  paidOn: string;
  note: string;
  createdBy: string | null;
  createdAt: string;
}

export interface MemberWeight {
  userId: string;
  weight: number;
  share: number;
  tasks: number;
}

/** A display-only currency: amounts stay in the main currency and this shows their equivalent. */
export interface FinanceCurrency {
  id: string;
  projectId: string;
  code: string;
  /** units of this currency per 1 unit of the main currency */
  rate: number;
  visible: boolean;
  position: number;
  createdAt: string;
  updatedAt: string;
}

export interface Finance {
  settings: FinanceSettings;
  currencies: FinanceCurrency[];
  /** optionId -> pay weight; options without an entry weigh 1 */
  weights: Record<string, number>;
  levels: ExperienceLevel[];
  /** userId -> experience level id */
  memberLevels: Record<string, string>;
  expenses: Expense[];
  payments: Payment[];
  payouts: Payout[];
  members: MemberMoney[];
  debts: Debt[];
  repayments: Repayment[];
  /** how the next received payment would be split, from the current Done tasks */
  currentShares: MemberWeight[];
}
